import { useCallback, useState } from "react"
import { useSnackbar } from "notistack"
import { Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { ApiQueryAutocomplete } from "../../../../../../../core/api/components/ApiQueryAutocomplete"
import { toDinero } from "../../../../../../currency/currencyLib"
import { useMoney } from "../../../../../../currency/useMoney"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import {
    ConfigQuery,
    ExtraServicesForUserResponse,
    fetchExtraServicesForUser,
} from "../../../../../../../shared/components/createOrder/createOrderApi"
import { ExtraServiceDetails } from "../../../../../../../shared/components/createOrder/createOrderTypes"
import {
    calculateServicePrice,
    isAutoAddedService,
} from "../../../../../../../shared/components/createOrder/components/drawer/extraServices/helpers"
import { AddProductInput, ProductionAddLookup, ProductionMeetingExtras } from "../useProductionOrderEdit"
import { ProductionMeetingDetailsFields, ProductionMeetingDefaults } from "./ProductionMeetingDetailsFields"

export type { ProductionMeetingDefaults } from "./ProductionMeetingDetailsFields"

// Catalog products and product wishes are typed straight into their order-line rows; this panel covers the
// remaining add flow — extra services, which are looked up by their own endpoint.
const useStyles = makeStyles()((theme) => ({
    panel: {
        padding: "1rem",
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: ".75rem",
    },
}))

type Props = Readonly<{
    lookup: ProductionAddLookup
    /** Seats on the delivery — PER_SEAT services are priced from it. */
    seatCount: number
    /** Product ids already on the edited delivery; a service can be added only once. */
    usedProductIds: ReadonlyArray<number>
    meetingDefaults: ProductionMeetingDefaults
    onAddExtraService: (input: AddProductInput, extras?: ProductionMeetingExtras) => void
    onClose: () => void
}>

export function ProductionAddLinePicker({
    lookup,
    seatCount,
    usedProductIds,
    meetingDefaults,
    onAddExtraService,
    onClose,
}: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { enqueueSnackbar } = useSnackbar()
    const moneyFactory = useMoney({})

    // A time/location-required service is not added straight away: the missing meeting details are asked
    // for right below the picker first, mirroring the MEETING_SERVICE flow in Create order.
    const [pendingService, setPendingService] = useState<ExtraServiceDetails | null>(null)

    // Mandatory and auto-added services are added by the platform itself, so they are not offered here.
    const selectSelectableServices = useCallback(
        (data: ExtraServicesForUserResponse): ExtraServiceDetails[] =>
            (data.extraServices ?? []).filter(
                (service) => !service.isMandatory && !isAutoAddedService(service.action, lookup.deliveryType),
            ),
        [lookup.deliveryType],
    )

    const servicesQuery = useApiQuery(fetchExtraServicesForUser, {
        queryName: ConfigQuery.EXTRA_SERVICES_FOR_USER,
        dependencies: {
            userId: lookup.userId,
            merchantId: lookup.merchantId,
            priceListId: lookup.priceListId,
            deliveryType: lookup.deliveryType,
            deliveryTime: lookup.deliveryTime,
        },
        select: selectSelectableServices,
    })

    const addService = (service: ExtraServiceDetails, extras?: ProductionMeetingExtras) =>
        onAddExtraService(
            { productId: service.id, name: service.name, price: calculateServicePrice(service, seatCount) },
            extras,
        )

    const handleServiceSelect = (option: ExtraServiceDetails | null) => {
        if (!option) {
            setPendingService(null)
            return
        }
        // The same service may exist only once per delivery, mirroring the product picker.
        if (usedProductIds.includes(option.id)) {
            enqueueSnackbar(l10n.getString("meeting-service-production-view-service-already-added-snackbar"), {
                variant: "warning",
            })
            return
        }
        if (option.isMeetingTimeRequired || option.isMeetingLocationRequired) {
            setPendingService(option)
            return
        }
        addService(option)
        onClose()
    }

    return (
        <Stack spacing=".75rem" className={classes.panel}>
            <ApiQueryAutocomplete<ExtraServiceDetails>
                fullWidth
                value={pendingService}
                options={servicesQuery.data ?? []}
                isLoading={servicesQuery.isInitialLoading}
                isError={servicesQuery.isError}
                onRetry={() => servicesQuery.refetch()}
                label={l10n.getString("meeting-service-production-view-add-extra-service-autocomplete-label")}
                getOptionLabel={(option) =>
                    `${option.name} — ${moneyFactory.format(toDinero(calculateServicePrice(option, seatCount)))}`
                }
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_event, option) => handleServiceSelect(option)}
            />

            {pendingService ? (
                <ProductionMeetingDetailsFields
                    service={pendingService}
                    lookup={lookup}
                    meetingDefaults={meetingDefaults}
                    onConfirm={(extras) => {
                        addService(pendingService, extras)
                        onClose()
                    }}
                    onCancel={onClose}
                />
            ) : (
                <Stack direction="row" spacing=".75rem" justifyContent="flex-end">
                    <LocalizedStrict id="meeting-service-production-view-add-picker-cancel">
                        <StandardButton fullWidth={false} variant="outlined" onClick={onClose}>
                            Cancel
                        </StandardButton>
                    </LocalizedStrict>
                </Stack>
            )}
        </Stack>
    )
}
